import { cn } from "app/lib/utils"
import type { PlayerControlSize } from "app/player/controlSizes"
import { getPlayerThickIcon } from "app/player/getPlayerThickIcon"
import { ComponentProps } from "react"
import PlayerCircleButton from "./PlayerCircleButton"
import PlayerIcon from "./PlayerIcon"
import usePlayToggleControls, {
  type PlayToggleState,
} from "./usePlayToggleControls"

type RoundPlayColor = NonNullable<
  ComponentProps<typeof PlayerCircleButton>["color"]
>

type IconRecipe = { src: string; size: number; className?: string }

const LIGHT_ICONS: Record<
  PlayerControlSize,
  Record<PlayToggleState, IconRecipe>
> = {
  sm: {
    play: {
      src: "/icons/player/ico_play_wh.svg",
      size: 30,
      className: "ml-[2px]",
    },
    pause: { src: "/icons/player/thick/ico_pause.svg", size: 24 },
    replay: {
      src: "/icons/player/thick/ico_replay.svg",
      size: 26,
      className: "rotate-180",
    },
  },
  md: {
    play: {
      src: "/icons/player/ico_play_wh.svg",
      size: 38,
      className: "ml-[2px]",
    },
    pause: { src: "/icons/player/thick/ico_pause.svg", size: 30 },
    replay: {
      src: "/icons/player/thick/ico_replay.svg",
      size: 32,
      className: "rotate-180",
    },
  },
}

function getWhiteIcons(
  size: PlayerControlSize
): Record<PlayToggleState, IconRecipe> {
  const base = LIGHT_ICONS[size]
  return {
    play: {
      src: "/icons/player/ico_play_black.svg",
      size: base.play.size,
    },
    pause: {
      ...base.pause,
      src: getPlayerThickIcon("white", "ico_pause.svg"),
    },
    replay: {
      ...base.replay,
      src: getPlayerThickIcon("white", "ico_replay.svg"),
    },
  }
}

/** Play-only icon recipe for compact initial play (same family as toggle). */
export const VIDEO_ROUND_PLAY_ONLY = {
  sm: LIGHT_ICONS.sm.play,
  md: LIGHT_ICONS.md.play,
} as const

type Props = Omit<ComponentProps<typeof PlayerCircleButton>, "children"> & {
  onReplayClick?: ComponentProps<typeof PlayerCircleButton>["onClick"]
  size?: PlayerControlSize
}

const PLAY_TOGGLE_STATES = ["play", "pause", "replay"] as const

/** Largest icon per size — keeps the button content box stable across states. */
const ICON_BOX_SIZE: Record<PlayerControlSize, number> = {
  sm: LIGHT_ICONS.sm.play.size,
  md: LIGHT_ICONS.md.play.size,
}

export default function VideoRoundPlayButton({
  onReplayClick,
  onClick,
  onMouseDown,
  onMouseLeave,
  className,
  size = "md",
  color = "neutraal",
  ...props
}: Props) {
  const { state, buttonProps } = usePlayToggleControls({
    onClick,
    onReplayClick,
    onMouseDown,
    onMouseLeave,
    className,
  })
  const icons = color === "white" ? getWhiteIcons(size) : LIGHT_ICONS[size]
  const resolvedColor = (color ?? "neutraal") as RoundPlayColor
  const boxSize = ICON_BOX_SIZE[size]

  return (
    <PlayerCircleButton
      color={resolvedColor}
      size={size}
      {...props}
      {...buttonProps}
    >
      <span
        className="relative inline-flex shrink-0 items-center justify-center"
        style={{ width: boxSize, height: boxSize }}
      >
        {/* Keep all icons mounted so toggling does not wait on a fresh <img> load. */}
        {PLAY_TOGGLE_STATES.map((key) => {
          const icon = icons[key]
          const isActive = state === key
          return (
            <PlayerIcon
              key={key}
              src={icon.src}
              size={icon.size}
              className={cn(
                "absolute",
                icon.className,
                !isActive && "invisible"
              )}
            />
          )
        })}
      </span>
    </PlayerCircleButton>
  )
}
